"use client";

import Link from "next/link";
import Image from "next/image";
import { useState, useMemo } from "react";
import type { StoreCardData } from "@/types/store";
import { getStoreLogoAlt } from "@/lib/storeLogo";

interface RelatedStoresProps {
  stores: StoreCardData[];
  perPage?: number; // allow customizing items per page
}

export default function RelatedStores({ stores, perPage = 12 }: RelatedStoresProps) {
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(stores.length / perPage));

  const paginatedStores = useMemo(() => {
    const start = (page - 1) * perPage;
    return stores.slice(start, start + perPage);
  }, [stores, page, perPage]);

  const visiblePages = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: Array<number | "ellipsis"> = [];
    const rangeStart = Math.max(1, page - 2);
    const rangeEnd = Math.min(totalPages, page + 2);

    if (rangeStart > 1) {
      pages.push(1);
      if (rangeStart > 2) pages.push("ellipsis");
    }

    for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i);

    if (rangeEnd < totalPages) {
      if (rangeEnd < totalPages - 1) pages.push("ellipsis");
      pages.push(totalPages);
    }

    return pages;
  }, [page, totalPages]);

  if (!stores || stores.length === 0) {
    return (
      <section className="py-16 bg-white text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-purple-700 mb-10">
          Related Stores
        </h2>
        <p className="text-gray-500">No related stores found.</p>
      </section>
    );
  }

  return (
    <section className="py-16 bg-white text-center">
      <h2 className="text-3xl md:text-4xl font-bold text-purple-700 mb-10">
        Related Stores
      </h2>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-8 justify-items-center px-4 md:px-16">
        {paginatedStores.map((store) => {
          // Handle both relative and absolute URLs
          const getImageUrl = () => {
            if (!store.image) return "/placeholder-store.png";
            // If already a full URL, use it as is
            if (store.image.startsWith("http://") || store.image.startsWith("https://")) {
              return store.image;
            }
            // If relative path, prepend domain
            return `https://www.itscoupons.com${store.image.startsWith("/") ? store.image : `/${store.image}`}`;
          };

          return (
            <Link
              key={store._id}
              href={`/stores/${store.slug}`}
              className="flex flex-col items-center justify-center bg-white overflow-hidden transition hover:scale-105 border border-gray-200 p-4"
              style={{ width: "178px", height: "178px" }} // square box
            >
              <div className="w-full h-[120px] flex items-center justify-center bg-white">
                <Image
                  src={getImageUrl()}
                  alt={getStoreLogoAlt(store.slug)}
                  title={`${store.name} Coupons & Deals`}
                  loading="lazy"
                  width={178}
                  height={120}
                  className="max-w-full max-h-full object-contain"
                  onError={(event) => {
                    const target = event.currentTarget as HTMLImageElement;
                    target.src = "/placeholder-store.png";
                  }}
                />
              </div>
              <div className="mt-3 text-sm font-semibold text-purple-800 text-center">
                {store.name}
              </div>
            </Link>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className={`px-3 py-1.5 rounded border text-sm ${
              page === 1
                ? "text-gray-400 border-gray-200 cursor-not-allowed"
                : "border-purple-700 text-purple-700 hover:bg-purple-50"
            }`}
          >
            Prev
          </button>

          {visiblePages.map((pageNum, idx) => {
            if (pageNum === "ellipsis") {
              return (
                <span key={`ellipsis-${idx}`} className="px-1 text-gray-400">
                  ...
                </span>
              );
            }

            const isActive = pageNum === page;
            return (
              <button
                key={pageNum}
                onClick={() => setPage(pageNum)}
                className={`h-9 min-w-[2.25rem] rounded border text-sm ${
                  isActive
                    ? "bg-purple-700 text-white border-purple-700"
                    : "border-gray-200 hover:bg-gray-50 text-gray-700"
                }`}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            disabled={page === totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className={`px-3 py-1.5 rounded border text-sm ${
              page === totalPages
                ? "text-gray-400 border-gray-200 cursor-not-allowed"
                : "border-purple-700 text-purple-700 hover:bg-purple-50"
            }`}
          >
            Next
          </button>
        </div>
      )}
    </section>
  );
}
